/**
 * Helpers de fechas. Semana ISO: lunes = 0.
 */

import { currentLocaleTag } from './formatting'

export function startOfDay(d: Date): Date {
  const r = new Date(d)
  r.setHours(0, 0, 0, 0)
  return r
}

export function endOfDay(d: Date): Date {
  const r = new Date(d)
  r.setHours(23, 59, 59, 999)
  return r
}

export function addDays(d: Date, days: number): Date {
  const r = new Date(d)
  r.setDate(r.getDate() + days)
  return r
}

/**
 * Suma meses conservando el día del mes, con "clamp" al último día del mes
 * destino cuando ese día no existe (31-ene + 1 mes = 28/29-feb, NO 2/3-mar).
 * Evita el desbordamiento silencioso de Date.setMonth, que al fijar el mes
 * sobre un día 29/30/31 salta al mes siguiente.
 */
export function addMonths(d: Date, months: number): Date {
  const r = new Date(d)
  const day = r.getDate()
  r.setDate(1) // fija el mes sin arrastrar el día → sin rollover
  r.setMonth(r.getMonth() + months)
  const lastDay = new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate()
  r.setDate(Math.min(day, lastDay))
  return r
}

/**
 * Fecha LOCAL (sin hora) como YYYY-MM-DD para input[type=date].
 * A diferencia de toISOString().slice(0,10) —que convierte a UTC y puede
 * desplazar el día en zonas horarias por delante de UTC (p. ej. Madrid de
 * madrugada)—, respeta el día que el usuario ve en su calendario.
 */
export function toDateInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function startOfWeek(d: Date): Date {
  const r = startOfDay(d)
  const day = r.getDay() // 0=Sun ... 6=Sat
  const diff = (day + 6) % 7 // distancia al lunes anterior
  return addDays(r, -diff)
}

export function endOfWeek(d: Date): Date {
  return endOfDay(addDays(startOfWeek(d), 6))
}

export function startOfMonth(d: Date): Date {
  return startOfDay(new Date(d.getFullYear(), d.getMonth(), 1))
}

export function endOfMonth(d: Date): Date {
  return endOfDay(new Date(d.getFullYear(), d.getMonth() + 1, 0))
}

/** Grid 6×7 que cubre todo el mes empezando en lunes. */
export function monthGridStart(d: Date): Date {
  return startOfWeek(startOfMonth(d))
}

export function monthGridEnd(d: Date): Date {
  return endOfWeek(endOfMonth(d))
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

export function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
}

export function dayLabelShort(d: Date): string {
  return d.toLocaleDateString(currentLocaleTag(), { weekday: 'short' })
}

export function monthLabel(d: Date): string {
  return d.toLocaleDateString(currentLocaleTag(), { month: 'long' })
}

export function formatTime(d: Date): string {
  return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
}

export function rangeLabel(from: Date, to: Date): string {
  const sameMonth = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()
  if (sameMonth) {
    return `${from.getDate()} – ${to.getDate()} ${monthLabel(from)} ${from.getFullYear()}`
  }
  return `${from.getDate()} ${monthLabel(from)} – ${to.getDate()} ${monthLabel(to)} ${to.getFullYear()}`
}

/**
 * Convierte un Date a string para input[type=datetime-local].
 * Mantiene la zona horaria local del navegador.
 */
export function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function fromLocalInput(s: string): Date {
  return new Date(s)
}

/**
 * El día de HOY como `YYYY-MM-DD`, en la zona del navegador.
 *
 * POR QUÉ NO `new Date().toISOString().slice(0, 10)`: `toISOString` convierte a
 * UTC, así que desde medianoche hasta las 02:00 de Madrid (01:00 en invierno)
 * devuelve el día ANTERIOR. Eso hacía que el selector de fecha de emisión de
 * facturas topara en el día de ayer y propusiera ayer por defecto.
 *
 * Se construye a mano con los métodos locales, que sí respetan la zona del
 * usuario, en lugar de formatear y volver a parsear.
 */
export function todayAsInputDate(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}
